import { useState } from "react";
import { toast } from "sonner";
import { Plus, UsersThree, CaretDown, CaretRight } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

function GroupRow({ g, contacts, onChanged }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(g.name);
  const [members, setMembers] = useState(g.member_ids);
  const dirty = name !== g.name || JSON.stringify(members) !== JSON.stringify(g.member_ids);
  const save = async (patch = {}) => {
    try { await api.put(`/service/recurring/groups/${g.id}`, { name, member_ids: members, active: g.active, ...patch }); toast.success("Group saved"); onChanged(); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not save group")); }
  };
  const flip = (id) => setMembers((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  return (
    <div className={`rounded-lg border ${g.active ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`recurring-group-row-${g.id}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setOpen(!open)} className="text-slate-400" data-testid={`recurring-group-expand-${g.id}`}>{open ? <CaretDown size={14} /> : <CaretRight size={14} />}</button>
        <UsersThree size={15} className="text-orange-500" />
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 flex-1" data-testid={`recurring-group-name-${g.id}`} />
        <span className="text-xs text-slate-500 w-20">{members.length} member{members.length === 1 ? "" : "s"}</span>
        <Switch checked={g.active} onCheckedChange={(v) => save({ active: v })} title="Active" data-testid={`recurring-group-active-${g.id}`} />
        <Button size="sm" disabled={!dirty || !name.trim()} onClick={() => save()} className="h-8 bg-orange-500 hover:bg-orange-600 text-white" data-testid={`recurring-group-save-${g.id}`}>Save</Button>
      </div>
      {open && (
        <div className="grid sm:grid-cols-2 gap-1 px-3 pb-3">
          {contacts.filter((c) => c.active || members.includes(c.id)).map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`recurring-group-${g.id}-member-${c.id}`}>
              <Checkbox checked={members.includes(c.id)} onCheckedChange={() => flip(c.id)} />{c.name}{!c.active && <span className="text-[10px] text-slate-400">(deactivated)</span>}
            </label>
          ))}
          {!contacts.length && <div className="text-xs text-slate-400">Add contacts first.</div>}
        </div>
      )}
    </div>
  );
}

export function GroupsTab({ groups, contacts, onChanged }) {
  const [name, setName] = useState("");
  const add = async () => {
    try { await api.post("/service/recurring/groups", { name, member_ids: [] }); setName(""); toast.success("Group created — expand it to add members"); onChanged(); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not create group")); }
  };
  return (
    <div className="space-y-2" data-testid="recurring-groups-tab">
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New group, e.g. Fire Safety Vendors" className="h-9" data-testid="recurring-group-new-name" />
        <Button size="sm" disabled={!name.trim()} onClick={add} className="h-9 bg-orange-500 hover:bg-orange-600 text-white" data-testid="recurring-group-add-btn"><Plus size={13} className="mr-1" />Create group</Button>
      </div>
      {!groups.length && <div className="text-sm text-slate-400 p-3">No groups yet.</div>}
      {groups.map((g) => <GroupRow key={`${g.id}-${g.updated_at || ""}-${g.active}`} g={g} contacts={contacts} onChanged={onChanged} />)}
    </div>
  );
}
